/**
 * APPOINTMENTS TAB
 * 
 * Manages appointments linked to a job work item.
 * Relationship: Job → Appointment via work_item_relationships (relationship_type: 'scheduled_for')
 * 
 * Features:
 * - List all appointments for a job
 * - Create new appointments
 * - Edit/delete appointments
 * - Status tracking (scheduled, confirmed, in_progress, completed, cancelled)
 * - Drag-to-reschedule (future enhancement)
 * 
 * @date December 12, 2025
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/appointments.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/appointments#the-appointments-tab)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {Calendar, Clock, Copy, Edit2,
  CheckCircle, XCircle, AlertCircle, RefreshCw,
  Navigation2, MapPinned, Search, ChevronDown, ChevronUp, ArrowUpDown,
  Lock, Plus, ShieldAlert} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { getAppointmentDisplayNumber } from '@/lib/appointmentDisplay';
import { copyTextToClipboard as copyTextToClipboardValue } from '@/lib/clipboard';
import { notify } from '@/lib/notifications';
import { emitOperationsGuidedTourTrainingEvent } from '@/lib/operations/guidedTourEvents';
import { useWorkItemDetailContext } from '@/contexts/WorkItemDetailContext';
import type { WorkItem } from '../UniversalWorkItems';
import {
  getAppointmentTimingStatus as getSharedAppointmentTimingStatus,
  getAppointmentTimingStyles as getSharedAppointmentTimingStyles,
} from '@/lib/appointmentCardPresentation';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import useAppointmentLiveTimers from '@/hooks/useAppointmentLiveTimers';
import { useOperationsAssignmentOptions } from '@/hooks/useOperationsAssignmentOptions';
import { formatTimeSAST, formatDateTimeSAST, SAST_TIMEZONE } from '@/lib/sastTime';
import {
  appointmentCompletionRulesAreOverridden,
  getAppointmentCompletionRequirementOverrides,
} from '@/lib/work-items/appointmentTypeConfig';
import AppointmentModal from '@/components/work-items/AppointmentModal';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';

import {
  getDefaultAppointmentWorkflowStatuses,
  getSuggestedProgressFromStatus,
  isTerminalWorkItemStatus,
  jobStatusBlocksAppointmentCreation,
  normalizeWorkflowStatusKey,
} from '@/lib/work-items/operationalStatusConfig';

// ============================================================================
// TYPES
// ============================================================================

// Appointment type - extends WorkItem but with flexible assigned_to for arrays
interface Appointment {
  id: string;
  item_number?: string;
  item_type: string;
  item_subtype?: string;
  title: string;
  description?: string;
  notes?: string;
  status: string;
  priority?: string;
  progress_percentage?: number;
  parent_job_number?: string | null;
  parent_title?: string | null;
  // Appointment-specific fields
  scheduled_start?: string;
  scheduled_end?: string;
  estimated_hours?: number;
  assigned_to?: number | number[] | null;
  assigned_team?: string | null;
  assigned_to_name?: string | null;
  assignees?: Array<{ user_id: number; first_name?: string; last_name?: string; email?: string }>;
  location_id?: number | null;
  location_name?: string | null;
  location_address?: string | null;
  location_coordinates?: { lat?: number; lng?: number; x?: number; y?: number } | string | null;
  contact_id?: number | null;
  contact_name?: string | null;
  custom_fields?: {
    // Actual timing from field worker
    travel_started_at?: string;
    arrived_at?: string;
    check_in_timestamp?: string;
    check_out_timestamp?: string;
    // Travel details
    travel_work_item_id?: string;
    travel_time_minutes?: number;
    completed_travel_entry?: {
      status: string;
      started_at?: string;
      ended_at?: string;
      duration_minutes?: number;
      distance_km?: number;
      start_location?: { lat: number; lng: number };
      end_location?: { lat: number; lng: number };
    };
    // GPS verification
    gps_check_in?: { lat: number; lng: number; accuracy?: number };
    gps_check_out?: { lat: number; lng: number; accuracy?: number };
    additional_notes?: string;
    equipment_required?: string;
    safety_requirements?: string;
    estimated_distance_km?: number;
    resolution?: string;
    [key: string]: unknown;
  };
  created_at?: string;
  updated_at?: string;
}

interface AppointmentsTabProps {
  workItemId: string;
  workItem: WorkItem;
  isActive: boolean;
  onUpdate?: () => void;
  onParentWorkItemUpdate?: (updates: Partial<WorkItem>) => void;
  userRole?: 'user' | 'supervisor' | 'admin' | 'owner' | 'root';
  /** Resolved `appointment.create` action permission for the job's workflow
   *  slice (Actions matrix / `operations_action_permissions`). Defaults to
   *  true so existing mounts without the resolver keep working. */
  canCreateAppointment?: boolean;
  initialAppointments?: Array<Pick<Appointment, 'id' | 'item_number' | 'title' | 'scheduled_start' | 'scheduled_end'> & { status?: string }>;
}

/** Stable default — `= []` in params creates a new array every render and retriggers seed sync effects. */
const EMPTY_INITIAL_APPOINTMENTS: NonNullable<AppointmentsTabProps['initialAppointments']> = [];

type AppointmentSortKey = 'scheduled_start' | 'title' | 'status' | 'assignee' | 'location' | 'updated_at';

type AppointmentSortDirection = 'asc' | 'desc';

type AppointmentSortOption = {
  value: `${AppointmentSortKey}:${AppointmentSortDirection}`;
  label: string;
  column: AppointmentSortKey;
  direction: AppointmentSortDirection;
};

type AppointmentTabParentWorkItem = WorkItem & {
  notes?: string | null;
  contact_person?: string | null;
  contact_person_id?: number | null;
  contact_person_id2?: number | null;
  contact_person_id3?: number | null;
  custom_fields?: WorkItem['custom_fields'] & {
    additional_notes?: string;
  };
};

type OperationsUserSummary = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
};

function mapOperationsUserToTechnician(user: OperationsUserSummary): { id: number; name: string; email: string } {
  return {
    id: user.id,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    email: user.email,
  };
}

function getErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

// ── Avatar helpers (mirrors WorkItemsDataGrid pattern) ──────────────────
const AVATAR_COLORS = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500',
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500',
];

function getAvatarColor(email?: string | null, firstName?: string | null): string {
  const colorIndex = ((email?.charCodeAt(0) || 0) + (firstName?.charCodeAt(0) || 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
}

function getAssigneeInitials(user: { first_name?: string | null; last_name?: string | null; email?: string | null }): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user.first_name) return user.first_name.substring(0, 2).toUpperCase();
  if (user.email) return user.email.substring(0, 2).toUpperCase();
  return '??';
}

function getAssigneeName(user: { first_name?: string | null; last_name?: string | null; email?: string | null }): string {
  if (user.first_name) return `${user.first_name} ${user.last_name || ''}`.trim();
  return user.email || 'Unknown';
}

function appointmentHasCompletionRulesOverride(appointment: Appointment): boolean {
  if (appointmentCompletionRulesAreOverridden(appointment)) {
    return true;
  }

  return Object.keys(getAppointmentCompletionRequirementOverrides(appointment)).length > 0;
}

function getPrimaryAppointmentAssigneeId(
  assignedTo: Appointment['assigned_to'] | string | undefined
): number | null {
  const primaryAssignedTo = Array.isArray(assignedTo)
    ? assignedTo[0]
    : assignedTo;

  const numericAssignedTo = typeof primaryAssignedTo === 'string'
    ? Number.parseInt(primaryAssignedTo, 10)
    : primaryAssignedTo;

  return typeof numericAssignedTo === 'number'
    && Number.isFinite(numericAssignedTo)
    && numericAssignedTo > 0
    ? numericAssignedTo
    : null;
}

// ============================================================================
// APPOINTMENT STATUS CONFIG
// ============================================================================

const APPOINTMENT_STATUS_ICONS: Record<string, React.ElementType> = {
  scheduled: Calendar,
  confirmed: CheckCircle,
  on_route: Navigation2,
  on_site: MapPinned,
  in_progress: Clock,
  ai_review: AlertCircle,
  ai_on_hold: Lock,
  resolved: CheckCircle,
  unresolved: AlertCircle,
  closed: CheckCircle,
  missed: XCircle,
  no_show: XCircle,
  cancelled: XCircle,
};

const APPOINTMENT_STATUSES = Array.from(
  new Map(
    [
      ...getDefaultAppointmentWorkflowStatuses().map((status) => ({
        value: status.key,
        label: status.label,
        color: status.color,
        icon: APPOINTMENT_STATUS_ICONS[normalizeWorkflowStatusKey(status.key)] || Clock,
      })),
      { value: 'rescheduled', label: 'Rescheduled', color: 'orange', icon: RefreshCw },
    ].map((status) => [normalizeWorkflowStatusKey(status.value), status])
  ).values()
);

const APPOINTMENT_COMPLETION_LIKE_RESOLUTION_KEYS = new Set(['partial', 'rescheduled']);

/** Outcomes where the scheduled visit never concluded — must never read as "completed". */
const APPOINTMENT_NON_COMPLETION_OUTCOME_KEYS = new Set(['cancelled', 'missed', 'no_show', 'rescheduled']);

const APPOINTMENT_SORT_OPTIONS: AppointmentSortOption[] = [
  { value: 'scheduled_start:desc', label: 'Newest scheduled first', column: 'scheduled_start', direction: 'desc' },
  { value: 'scheduled_start:asc', label: 'Oldest scheduled first', column: 'scheduled_start', direction: 'asc' },
  { value: 'updated_at:desc', label: 'Recently updated', column: 'updated_at', direction: 'desc' },
  { value: 'updated_at:asc', label: 'Least recently updated', column: 'updated_at', direction: 'asc' },
  { value: 'title:asc', label: 'Appointment A–Z', column: 'title', direction: 'asc' },
  { value: 'title:desc', label: 'Appointment Z–A', column: 'title', direction: 'desc' },
  { value: 'assignee:asc', label: 'Assignment A–Z', column: 'assignee', direction: 'asc' },
  { value: 'status:asc', label: 'Status A–Z', column: 'status', direction: 'asc' },
];

const formatSubtypeLabel = (value: string) => value.replace(/[_-]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

// ============================================================================
// TRAVEL TIME DISPLAY COMPONENT
// ============================================================================

/**
 * Copy text to clipboard with visual feedback
 */
const copyToClipboard = async (text: string, label: string = 'Text') => {
  try {
    await copyTextToClipboardValue(text);
    notify.success(`${label} copied to clipboard`);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    notify.error('Failed to copy to clipboard');
  }
};

function TravelTimeDisplay({
  travelMinutes,
  departureTime,
  isDark
}: {
  travelMinutes?: number;
  departureTime?: string;
  isDark: boolean;
}) {
  if (!travelMinutes || travelMinutes <= 0) return null;

  const hours = Math.floor(travelMinutes / 60);
  const mins = travelMinutes % 60;
  const formattedDuration = hours > 0
    ? `${hours}h ${mins}m`
    : `${mins} min`;

  const formattedDeparture = departureTime
    ? formatTimeSAST(departureTime)
    : null;

  return (
    <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
      <span className="flex items-center gap-1">
        🚗 {formattedDuration} travel
      </span>
      {formattedDeparture && (
        <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          (depart by {formattedDeparture})
        </span>
      )}
    </div>
  );
}

// ============================================================================
// APPOINTMENTS TAB COMPONENT
// ============================================================================

export default function AppointmentsTab({
  workItemId,
  workItem,
  isActive,
  onUpdate,
  onParentWorkItemUpdate,
  userRole = 'user',
  canCreateAppointment = true,
  initialAppointments = EMPTY_INITIAL_APPOINTMENTS,
}: AppointmentsTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const {
    users: assignmentUsers,
    teams: operationsTeams,
    loading: loadingAssignmentOptions,
    getTeamById,
  } = useOperationsAssignmentOptions();
  // Live appointment timers — subscribes to operations WebSocket channel
  const { getTimer, seedFromAppointments, getStatus } = useAppointmentLiveTimers();
  const parentWorkItem = workItem as AppointmentTabParentWorkItem;
  const isJobParent = workItem?.item_type === 'job';
  const parentNoun = isJobParent ? 'job' : (workItem?.item_subtype || workItem?.item_type || 'item').replace(/_/g, ' ');

  // Check if user can edit past appointments (admin/owner/root only)
  const canEditPastAppointments = userRole === 'admin' || userRole === 'owner' || userRole === 'root';
  // Completed/done/closed/cancelled jobs cannot receive new appointments
  // regardless of the `appointment.create` permission.
  const jobBlocksNewAppointments = jobStatusBlocksAppointmentCreation(workItem?.status);
  const detailContext = useWorkItemDetailContext();

  const seededAppointments = useMemo<Appointment[]>(
    () => initialAppointments.map((appointment) => ({
      id: appointment.id,
      item_number: appointment.item_number,
      item_type: 'appointment',
      title: appointment.title,
      status: appointment.status || 'scheduled',
      scheduled_start: appointment.scheduled_start || undefined,
      scheduled_end: appointment.scheduled_end || undefined,
    })),
    [initialAppointments]
  );
  const seededAppointmentsRef = useRef(seededAppointments);
  const [appointments, setAppointments] = useState<Appointment[]>(seededAppointments);
  const [loading, setLoading] = useState(true);
  const [appointmentSearch, setAppointmentSearch] = useState('');
  const [appointmentSort, setAppointmentSort] = useState<{ column: AppointmentSortKey; direction: AppointmentSortDirection }>({
    column: 'title',
    direction: 'desc',
  });
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [technicians, setTechnicians] = useState<Array<{ id: number; name: string; email: string }>>([]);

  // State for full appointment detail modal (replaces inline edit form)
  const [viewingAppointment, setViewingAppointment] = useState<WorkItem | null>(null);
  const [viewingAppointmentModalOpen, setViewingAppointmentModalOpen] = useState(false);

  // Load appointments
  const loadAppointments = useCallback(async (options?: { silent?: boolean }) => {
    if (!isActive) return;

    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const tokens = authService.getTokens();
      if (!tokens) {
        notify.error('Please log in to view appointments');
        return;
      }

      const res = await fetch('/api/v1/work-item-relationships', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          action: 'getAppointmentsForJob',
          jobId: workItemId
        })
      });

      const rawResponse = await res.text();
      const data = rawResponse
        ? (() => {
          try {
            return JSON.parse(rawResponse);
          } catch (parseError) {
            console.error('[AppointmentsTab] Failed to parse appointments response:', parseError, {
              status: res.status,
              statusText: res.statusText,
              bodyPreview: rawResponse.slice(0, 300),
            });
            return null;
          }
        })()
        : null;

      if (!res.ok) {
        throw new Error(data?.error || `Failed to load appointments (${res.status})`);
      }

      if (!data || typeof data !== 'object') {
        throw new Error('Appointments service returned an empty or invalid response');
      }

      if (data.success) {
        const nextAppointments = Array.isArray(data.data) ? data.data : [];
        setAppointments(nextAppointments);
        // Seed live timers only when there is actual appointment data to process.
        if (nextAppointments.length > 0) {
          seedFromAppointments(nextAppointments);
        }
      } else {
        notify.error(data.error || 'Failed to load appointments');
      }
    } catch (error: unknown) {
      console.error('Error loading appointments:', error);
      notify.error(getErrorMessage(error) || 'Failed to load appointments');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [isActive, seedFromAppointments, workItemId]);

  // Load technicians for assignment (uses shared context to avoid duplicate API call)
  const loadTechnicians = useCallback(async () => {
    try {
      if (assignmentUsers.length > 0) {
        const techList = (assignmentUsers as OperationsUserSummary[]).map(mapOperationsUserToTechnician);
        setTechnicians(techList);
        return;
      }

      // Keep the previous shared-context fallback for early renders while the hook hydrates.
      if (detailContext) {
        const users = await detailContext.loadOperationsUsers() as OperationsUserSummary[];
        const techList = users.map(mapOperationsUserToTechnician);
        setTechnicians(techList);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  }, [assignmentUsers, detailContext]);

  useEffect(() => {
    seededAppointmentsRef.current = seededAppointments;
  }, [seededAppointments]);

  useEffect(() => {
    setAppointments((prev) => {
      if (prev.length !== seededAppointments.length) return seededAppointments;
      const unchanged = prev.every((a, i) => {
        const s = seededAppointments[i];
        return a.id === s.id
          && a.status === s.status
          && (a.scheduled_start ?? null) === (s.scheduled_start ?? null)
          && (a.scheduled_end ?? null) === (s.scheduled_end ?? null);
      });
      return unchanged ? prev : seededAppointments;
    });
    // Do not drive `loading` from seed rows: "no prefetch" stays length 0 even after the API
    // returns, and the old logic set loading back to true every time this effect ran (infinite spinner).
  }, [seededAppointments, workItemId]);

  // Keep refs to the latest load callbacks so the main data-loading effect does not
  // thrash when the surrounding detail context re-renders.
  const loadTechniciansRef = useRef(loadTechnicians);
  useEffect(() => { loadTechniciansRef.current = loadTechnicians; }, [loadTechnicians]);
  const loadAppointmentsRef = useRef(loadAppointments);
  useEffect(() => { loadAppointmentsRef.current = loadAppointments; }, [loadAppointments]);

  // Load data when tab becomes active OR when workItemId changes
  useEffect(() => {
    if (isActive) {
      const hasSeedData = seededAppointmentsRef.current.length > 0;
      setAppointments(hasSeedData ? seededAppointmentsRef.current : []);
      setLoading(!hasSeedData);
      void loadAppointmentsRef.current?.({ silent: hasSeedData });
      void loadTechniciansRef.current?.();
    }
  }, [isActive, workItemId]);

  // Open full appointment detail modal instead of inline edit form
  const handleOpenAppointmentModal = (appointment: Appointment) => {
    // Convert Appointment to WorkItem format for the modal
    const appointmentAsWorkItem: WorkItem = {
      id: appointment.id,
      item_number: appointment.item_number || '',
      item_type: 'appointment',
      item_subtype: appointment.item_subtype || null,
      title: appointment.title,
      description: appointment.description,
      status: appointment.status,
      priority: (appointment.priority as WorkItem['priority']) || 'medium',
      assigned_to: Array.isArray(appointment.assigned_to) ? appointment.assigned_to[0] : (appointment.assigned_to || null),
      assigned_team: appointment.assigned_team || null,
      assignees: appointment.assignees || [],
      created_by: 0, // Will be resolved by the modal
      scheduled_start: appointment.scheduled_start || null,
      scheduled_end: appointment.scheduled_end || null,
      estimated_hours: appointment.estimated_hours || null,
      location_id: appointment.location_id || null,
      location_name: appointment.location_name || null,
      location_address: appointment.location_address || null,
      location_coordinates: appointment.location_coordinates || null,
      contact_id: appointment.contact_id || null,
      contact_name: appointment.contact_name || null,
      custom_fields: appointment.custom_fields || {},
      progress_percentage: getSuggestedProgressFromStatus(appointment.status, appointment.progress_percentage) ?? appointment.progress_percentage ?? 0,
      tags: [],
      created_at: appointment.created_at || '',
      updated_at: appointment.updated_at || '',
      // Parent job reference
      parent_id: workItemId,
      parent_job_number: workItem.item_number || null,
      parent_title: workItem.title || null,
    };

    setViewingAppointment(appointmentAsWorkItem);
    setViewingAppointmentModalOpen(true);
  };


  // Format datetime for display
  const formatDateTime = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return formatDateTimeSAST(dateStr, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Check if appointment is in the past
  const isPastAppointment = (appt: Appointment): boolean => {
    if (!appt.scheduled_start) return false;
    const scheduledStart = new Date(appt.scheduled_start);
    return scheduledStart < new Date();
  };

  const isAppointmentOperationallyComplete = (appt: Appointment): boolean => {
    const normalizedStatus = normalizeWorkflowStatusKey(appt.status);
    const reviewStatuses = ['ai_review', 'ai_on_hold'];
    const resolution = typeof appt.custom_fields?.resolution === 'string'
      ? normalizeWorkflowStatusKey(appt.custom_fields.resolution)
      : null;

    return Boolean(
      appt.custom_fields?.check_out_timestamp
      || reviewStatuses.includes(normalizedStatus)
      || isTerminalWorkItemStatus(normalizedStatus)
      || (resolution && (isTerminalWorkItemStatus(resolution) || APPOINTMENT_COMPLETION_LIKE_RESOLUTION_KEYS.has(resolution)))
    );
  };

  const getAppointmentTimingStatus = (appt: Appointment) => getSharedAppointmentTimingStatus(appt);

  const getTimingStatusStyles = (status: ReturnType<typeof getSharedAppointmentTimingStatus>) => getSharedAppointmentTimingStyles(status, isDark);

  // Check if appointment has been worked (has actual times)
  const hasActualTimes = (appt: Appointment): boolean => {
    return !!(appt.custom_fields?.check_in_timestamp || appt.custom_fields?.arrived_at);
  };

  // Format time only
  const formatTimeOnly = (dateStr: string | undefined) => {
    if (!dateStr) return '--:--';
    return formatTimeSAST(dateStr);
  };

  // Calculate actual duration worked
  const calculateActualDuration = (appt: Appointment): string => {
    const checkIn = appt.custom_fields?.check_in_timestamp || appt.custom_fields?.arrived_at;
    const checkOut = appt.custom_fields?.check_out_timestamp;
    if (!checkIn || !checkOut) return '';
    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    const hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    return `${hours.toFixed(1)}h`;
  };

  // Calculate SmartTask timer durations from custom_fields timestamps
  // en_route duration: travel_started_at → arrived_at (or check_in_timestamp)
  // in_progress duration: check_in_timestamp → check_out_timestamp
  const getSmartTaskDurations = (appt: Appointment): { enRouteMinutes: number | null; inProgressMinutes: number | null; travelDistanceKm: number | null } => {
    const cf = appt.custom_fields;
    if (!cf) return { enRouteMinutes: null, inProgressMinutes: null, travelDistanceKm: null };

    // En route: prefer completed_travel_entry.duration_minutes (SmartTask travel tracking)
    let enRouteMinutes: number | null = null;
    let travelDistanceKm: number | null = null;
    if (cf.completed_travel_entry?.duration_minutes) {
      enRouteMinutes = cf.completed_travel_entry.duration_minutes;
      if (typeof cf.completed_travel_entry.distance_km === 'number') {
        travelDistanceKm = cf.completed_travel_entry.distance_km;
      }
    } else if (cf.travel_time_minutes) {
      enRouteMinutes = cf.travel_time_minutes;
    } else if (cf.travel_started_at) {
      const travelEnd = cf.arrived_at || cf.check_in_timestamp;
      if (travelEnd) {
        enRouteMinutes = Math.round((new Date(travelEnd).getTime() - new Date(cf.travel_started_at).getTime()) / 60000);
      }
    }

    // Fallback: estimated distance from Valhalla (stored at travel start)
    if (travelDistanceKm == null && typeof cf.estimated_distance_km === 'number') {
      travelDistanceKm = cf.estimated_distance_km;
    }

    // In progress: from check_in_timestamp to check_out_timestamp
    let inProgressMinutes: number | null = null;
    if (cf.check_in_timestamp && cf.check_out_timestamp) {
      inProgressMinutes = Math.round((new Date(cf.check_out_timestamp).getTime() - new Date(cf.check_in_timestamp).getTime()) / 60000);
    }

    return { enRouteMinutes, inProgressMinutes, travelDistanceKm };
  };

  // Get status config
  const getStatusConfig = (status: string) => {
    const normalizedStatus = normalizeWorkflowStatusKey(status);
    return APPOINTMENT_STATUSES.find(s => normalizeWorkflowStatusKey(s.value) === normalizedStatus) || APPOINTMENT_STATUSES[0];
  };

  const getAppointmentTeam = useCallback((appointment: Appointment) => {
    if (!appointment.assigned_team) {
      return null;
    }

    return getTeamById(appointment.assigned_team) || null;
  }, [getTeamById]);

  const getAppointmentAssigneeDisplayName = useCallback((appointment: Appointment): string => {
    const appointmentTeam = getAppointmentTeam(appointment);
    if (appointmentTeam?.team_name) {
      return appointmentTeam.team_name;
    }

    const directAssignedName = typeof appointment.assigned_to_name === 'string' && appointment.assigned_to_name.trim().length > 0
      ? appointment.assigned_to_name.trim()
      : null;

    if (directAssignedName) {
      return directAssignedName;
    }

    const assignedId = Array.isArray(appointment.assigned_to)
      ? appointment.assigned_to[0]
      : appointment.assigned_to;

    const technician = assignedId != null
      ? technicians.find(tech => tech.id === Number(assignedId))
      : null;

    if (technician?.name) {
      return technician.name;
    }

    const matchedAssignee = Array.isArray(appointment.assignees) && appointment.assignees.length > 0
      ? (assignedId != null
        ? appointment.assignees.find(assignee => Number(assignee.user_id) === Number(assignedId)) ?? appointment.assignees[0]
        : appointment.assignees[0])
      : null;

    const assigneeName = matchedAssignee
      ? `${matchedAssignee.first_name || ''} ${matchedAssignee.last_name || ''}`.trim() || matchedAssignee.email || null
      : null;

    if (assigneeName) {
      return assigneeName;
    }

    if (appointment.title?.includes(' — ')) {
      const titleSegments = appointment.title.split(' — ').map(segment => segment.trim()).filter(Boolean);
      const trailingSegment = titleSegments.at(-1);
      if (trailingSegment && trailingSegment !== appointment.title.trim()) {
        return trailingSegment;
      }
    }

    return assignedId != null ? `User #${assignedId}` : 'Unassigned';
  }, [getAppointmentTeam, technicians]);

  /**
   * Build a rich assignee list for avatar rendering.
   * Tries assignees array first, then falls back to technicians lookup,
   * then to assigned_to_name, mirroring getAppointmentAssigneeDisplayName.
   */
  const getAppointmentAssigneeAvatars = useCallback((appointment: Appointment): Array<{ user_id: number; first_name?: string | null; last_name?: string | null; email?: string | null; displayName: string }> => {
    const assignedIds = Array.isArray(appointment.assigned_to)
      ? appointment.assigned_to
      : appointment.assigned_to != null
        ? [appointment.assigned_to]
        : [];

    // Source 1: appointment.assignees (richest data)
    if (Array.isArray(appointment.assignees) && appointment.assignees.length > 0) {
      return appointment.assignees.map(a => ({
        user_id: a.user_id,
        first_name: a.first_name ?? null,
        last_name: a.last_name ?? null,
        email: a.email ?? null,
        displayName: getAssigneeName(a),
      }));
    }

    // Source 2: technicians lookup from assigned_to IDs
    const results: Array<{ user_id: number; first_name?: string | null; last_name?: string | null; email?: string | null; displayName: string }> = [];
    for (const rawId of assignedIds) {
      const id = Number(rawId);
      if (!Number.isFinite(id) || id <= 0) continue;
      const tech = technicians.find(t => t.id === id);
      if (tech) {
        const nameParts = tech.name.split(' ');
        results.push({
          user_id: id,
          first_name: nameParts[0] || null,
          last_name: nameParts.slice(1).join(' ') || null,
          email: tech.email || null,
          displayName: tech.name,
        });
      }
    }
    if (results.length > 0) {
      return results;
    }

    // Source 3: assigned_to_name string
    if (typeof appointment.assigned_to_name === 'string' && appointment.assigned_to_name.trim()) {
      const nameParts = appointment.assigned_to_name.trim().split(' ');
      return [{
        user_id: assignedIds[0] ?? 0,
        first_name: nameParts[0] || null,
        last_name: nameParts.slice(1).join(' ') || null,
        email: null,
        displayName: appointment.assigned_to_name.trim(),
      }];
    }

    return [];
  }, [technicians]);

  const getAppointmentLocationLabel = useCallback((appointment: Appointment) => {
    return appointment.location_name || appointment.location_address || 'No location set';
  }, []);

  const getAppointmentSearchText = useCallback((appointment: Appointment) => {
    return [
      appointment.title,
      appointment.item_number,
      appointment.item_subtype,
      appointment.status,
      appointment.contact_name,
      appointment.location_name,
      appointment.location_address,
      appointment.parent_job_number,
      appointment.parent_title,
      appointment.assigned_to_name,
      getAppointmentAssigneeDisplayName(appointment),
      getAppointmentTeam(appointment)?.team_name,
      getAppointmentDisplayNumber(appointment),
    ]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .join(' ')
      .toLowerCase();
  }, [getAppointmentAssigneeDisplayName, getAppointmentTeam]);

  const getAppointmentSortValue = useCallback((appointment: Appointment, column: AppointmentSortKey): string | number => {
    switch (column) {
      case 'scheduled_start': {
        const value = appointment.scheduled_start ? new Date(appointment.scheduled_start).getTime() : 0;
        return Number.isNaN(value) ? 0 : value;
      }
      case 'status':
        return normalizeWorkflowStatusKey(appointment.status || '');
      case 'assignee':
        return getAppointmentAssigneeDisplayName(appointment).toLowerCase();
      case 'location':
        return getAppointmentLocationLabel(appointment).toLowerCase();
      case 'updated_at': {
        const value = appointment.updated_at ? new Date(appointment.updated_at).getTime() : 0;
        return Number.isNaN(value) ? 0 : value;
      }
      case 'title':
      default:
        return `${getAppointmentDisplayNumber(appointment) || ''} ${appointment.title || ''}`.trim().toLowerCase();
    }
  }, [getAppointmentAssigneeDisplayName, getAppointmentLocationLabel]);

  const effectiveAppointments = useMemo(() => appointments.map((appointment) => {
    const effectiveStatus = getStatus(appointment.id) || appointment.status;

    return effectiveStatus === appointment.status
      ? appointment
      : { ...appointment, status: effectiveStatus };
  }), [appointments, getStatus]);

  const filteredAppointments = useMemo(() => {
    const normalizedSearch = appointmentSearch.trim().toLowerCase();
    const results = effectiveAppointments.filter((appointment) => {
      if (!normalizedSearch) {
        return true;
      }

      return getAppointmentSearchText(appointment).includes(normalizedSearch);
    });

    results.sort((left, right) => {
      const leftValue = getAppointmentSortValue(left, appointmentSort.column);
      const rightValue = getAppointmentSortValue(right, appointmentSort.column);

      let comparison = 0;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (comparison === 0) {
        const leftDate = left.scheduled_start ? new Date(left.scheduled_start).getTime() : 0;
        const rightDate = right.scheduled_start ? new Date(right.scheduled_start).getTime() : 0;
        comparison = leftDate - rightDate;
      }

      return appointmentSort.direction === 'asc' ? comparison : comparison * -1;
    });

    return results;
  }, [appointmentSearch, appointmentSort, effectiveAppointments, getAppointmentSearchText, getAppointmentSortValue]);

  const legacyGroupSizes = useMemo(() => new Map<string, number>(), []);

  const handleAppointmentSort = useCallback((column: AppointmentSortKey) => {
    setAppointmentSort((currentSort) => {
      if (currentSort.column === column) {
        return {
          column,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        column,
        direction: column === 'scheduled_start' || column === 'updated_at' ? 'desc' : 'asc',
      };
    });
  }, []);

  const activeAppointmentSortOption = useMemo(() => {
    return APPOINTMENT_SORT_OPTIONS.find((option) => (
      option.column === appointmentSort.column && option.direction === appointmentSort.direction
    )) || APPOINTMENT_SORT_OPTIONS[0];
  }, [appointmentSort.column, appointmentSort.direction]);

  const handleAppointmentSortOptionChange = useCallback((value: string) => {
    const nextSort = APPOINTMENT_SORT_OPTIONS.find((option) => option.value === value);
    if (!nextSort) {
      return;
    }

    setAppointmentSort({
      column: nextSort.column,
      direction: nextSort.direction,
    });
  }, []);

  const renderAppointmentSortIcon = useCallback((column: AppointmentSortKey) => {
    if (appointmentSort.column !== column) {
      return <ArrowUpDown className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />;
    }

    return appointmentSort.direction === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-blue-500" />
      : <ChevronDown className="h-3.5 w-3.5 text-blue-500" />;
  }, [appointmentSort.column, appointmentSort.direction, isDark]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-tour-id="work-item-appointments-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            {isJobParent ? 'Appointments' : 'Visits'} <UserDocsLink docsPath="/total-operations/workflows/service-operations/appointments#the-appointments-tab" label="User guide: Appointments" className="ml-1 align-middle" />
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Schedule and manage {isJobParent ? 'appointments' : 'visits'} for this {parentNoun}
          </p>
        </div>
        {canCreateAppointment && (
          <button
            data-tour-id="appointments-tab-new-appointment"
            type="button"
            disabled={jobBlocksNewAppointments}
            title={jobBlocksNewAppointments ? `Cannot create appointments for completed or closed ${parentNoun}s.` : undefined}
            onClick={() => {
              setShowAppointmentModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            New {isJobParent ? 'Appointment' : 'Visit'}
          </button>
        )}
      </div>


      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        onSuccess={() => {
          setShowAppointmentModal(false);
          void loadAppointments({ silent: true });
          onUpdate?.();
        }}
        fromJobContext={true}
        workflowContext={workItem?.module_context || null}
        moduleContext={workItem?.module_context || null}
        parentJob={workItem ? {
          id: workItem.id,
          item_number: workItem.item_number || '',
          title: workItem.title || '',
          description: workItem.description || '',
          notes: parentWorkItem.notes || '',
          item_type: workItem.item_type,
          item_subtype: workItem.item_subtype,
          status: workItem.status,
          module_context: workItem.module_context,
          contact_id: workItem.contact_id,
          contact_name: workItem.contact_name,
          contact_person: parentWorkItem.contact_person,
          contact_person_id: parentWorkItem.contact_person_id,
          contact_person_id2: parentWorkItem.contact_person_id2,
          contact_person_id3: parentWorkItem.contact_person_id3,
          location_id: workItem.location_id,
          location_name: workItem.location_name,
          location_address: workItem.location_address,
          location_coordinates: workItem.location_coordinates,
          custom_fields: workItem.custom_fields,
        } : null}
      />

      {/* Appointments List */}
      {appointments.length === 0 ? (
        <div className={`text-center py-12 rounded-lg border-2 border-dashed ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
          }`}>
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No {isJobParent ? 'appointments' : 'visits'} yet</p>
          <p className="text-sm mt-1">Create your first {isJobParent ? 'appointment' : 'visit'} to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {legacyGroupSizes.size > 0 && (
            <div className={`rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-amber-800/60 bg-amber-900/20 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              {legacyGroupSizes.size} legacy linked appointment set{legacyGroupSizes.size === 1 ? '' : 's'} are still visible individually while older data is phased out.
            </div>
          )}

          <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-gray-50/80'}`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Appointment grid
                  </div>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Search by appointment number, title, team member, team, contact, location, or status — then sort the grid to find the right visit fast.
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <label className="relative block min-w-[260px]">
                    <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      type="search"
                      value={appointmentSearch}
                      onChange={(event) => setAppointmentSearch(event.target.value)}
                      placeholder="Search appointments..."
                      className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
                    />
                  </label>

                  <label className={`relative flex min-w-[220px] items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                    <ArrowUpDown className={`h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className="sr-only">Sort appointments</span>
                    <select
                      value={activeAppointmentSortOption.value}
                      onChange={(event) => handleAppointmentSortOptionChange(event.target.value)}
                      className={`w-full appearance-none bg-transparent pr-6 text-sm font-medium focus:outline-none ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                      aria-label="Sort appointments"
                    >
                      {APPOINTMENT_SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  </label>

                  <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                    <span className="font-medium">{filteredAppointments.length}</span>
                    <span>of {appointments.length} shown</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={isDark ? 'bg-gray-800/80' : 'bg-gray-50'}>
                  <tr>
                    {[
                      { key: 'title', label: 'Appointment' },
                      { key: 'scheduled_start', label: 'Schedule' },
                      { key: 'status', label: 'Status' },
                      { key: 'assignee', label: 'Assignment' },
                      { key: 'location', label: 'Customer & location' },
                      { key: 'updated_at', label: 'Progress' },
                    ].map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300 hover:bg-gray-700/70' : 'text-gray-600 hover:bg-gray-100'} ${column.key !== 'updated_at' ? 'cursor-pointer select-none' : 'cursor-pointer select-none'} transition-colors`}
                        onClick={() => handleAppointmentSort(column.key as AppointmentSortKey)}
                      >
                        <div className="flex items-center gap-1.5">
                          <span>{column.label}</span>
                          {renderAppointmentSortIcon(column.key as AppointmentSortKey)}
                        </div>
                      </th>
                    ))}
                    <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-800 bg-gray-900' : 'divide-gray-100 bg-white'}`}>
                  {filteredAppointments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <Search className={`mx-auto mb-3 h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>No appointments matched your search</p>
                        <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Try another appointment number, contact, technician, team, or status.</p>
                      </td>
                    </tr>
                  ) : filteredAppointments.map((appt) => {
                    const statusConfig = getStatusConfig(appt.status);
                    const timingStatus = getAppointmentTimingStatus(appt);
                    const timingStyles = getTimingStatusStyles(timingStatus);
                    const liveTimer = getTimer(appt.id);
                    const canEdit = !isPastAppointment(appt) || canEditPastAppointments;
                    const isOperationallyComplete = isAppointmentOperationallyComplete(appt);
                    const normalizedApptStatus = normalizeWorkflowStatusKey(appt.status);
                    const outcomeResolution = normalizedApptStatus === 'unresolved'
                      ? normalizeWorkflowStatusKey(typeof appt.custom_fields?.resolution === 'string' ? appt.custom_fields.resolution : '')
                      : '';
                    const visitDidNotConclude = APPOINTMENT_NON_COMPLETION_OUTCOME_KEYS.has(normalizedApptStatus)
                      || APPOINTMENT_NON_COMPLETION_OUTCOME_KEYS.has(outcomeResolution);
                    const operationalCaption = !isOperationallyComplete
                      ? 'Waiting on field activity'
                      : visitDidNotConclude && !appt.custom_fields?.check_out_timestamp
                        ? 'No field visit completed'
                        : 'Operationally complete';
                    const hasCompletionOverride = appointmentHasCompletionRulesOverride(appt);
                    const legacyGroupId = null;
                    const legacyGroupSize = 0;
                    const { enRouteMinutes, inProgressMinutes, travelDistanceKm } = getSmartTaskDurations(appt);
                    const actualDuration = calculateActualDuration(appt);
                    const assigneeName = getAppointmentAssigneeDisplayName(appt);
                    const appointmentTeam = getAppointmentTeam(appt);
                    const scheduleSummary = appt.scheduled_start
                      ? `${formatDateTime(appt.scheduled_start)}${appt.scheduled_end ? ` → ${formatTimeOnly(appt.scheduled_end)}` : ''}`
                      : 'Unscheduled';
                    const displayNumber = getAppointmentDisplayNumber(appt);

                    return (
                      <tr
                        key={appt.id}
                        className={`${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'} transition-colors ${canEdit ? 'cursor-pointer' : ''}`}
                        onClick={canEdit ? () => handleOpenAppointmentModal(appt) : undefined}
                      >
                        <td className="px-4 py-3 align-top">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (displayNumber) {
                                    void copyToClipboard(displayNumber, 'Appointment number');
                                  }
                                }}
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono ${isDark ? 'bg-gray-800 text-blue-300 hover:bg-gray-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'} transition-colors`}
                                title="Copy appointment number"
                              >
                                <Copy className="h-3 w-3" />
                                {displayNumber || 'No number'}
                              </button>
                              {appt.priority && appt.priority !== 'medium' && (
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${appt.priority === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : appt.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                                  {formatSubtypeLabel(appt.priority)}
                                </span>
                              )}
                              {hasCompletionOverride && (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                                  <ShieldAlert className="h-3 w-3" />
                                  Override
                                </span>
                              )}
                            </div>
                            <div>
                              <p className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{appt.title}</p>
                              <div className={`mt-1 flex flex-wrap items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span>{formatSubtypeLabel(appt.item_subtype || 'appointment')}</span>
                                {legacyGroupSize > 1 && <span>· {legacyGroupSize} linked legacy appointments</span>}
                                {appt.parent_job_number && <span>· Parent job {appt.parent_job_number}</span>}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="space-y-2">
                            <div className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${timingStyles.bg} ${timingStyles.border}`}>
                              {timingStyles.label}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{scheduleSummary}</div>
                            <TravelTimeDisplay
                              travelMinutes={appt.custom_fields?.travel_time_minutes || undefined}
                              departureTime={appt.custom_fields?.travel_started_at}
                              isDark={isDark}
                            />
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="space-y-2">
                            <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${statusConfig.color === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : statusConfig.color === 'orange' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : statusConfig.color === 'blue' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : statusConfig.color === 'green' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                              {statusConfig.label}
                            </span>
                            {liveTimer && !isOperationallyComplete ? (
                              <div className={`text-xs font-medium ${liveTimer.isOverSchedule ? 'text-red-500' : isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                                {liveTimer.activeState.replace(/_/g, ' ')} · {liveTimer.elapsedFormatted}
                              </div>
                            ) : (
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                {operationalCaption}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-col gap-1.5">
                            {/* Avatar stack */}
                            {(() => {
                              const avatarAssignees = getAppointmentAssigneeAvatars(appt);
                              if (avatarAssignees.length > 0) {
                                return (
                                  <div className="flex items-center gap-1.5">
                                    <div className="flex -space-x-1.5">
                                      {avatarAssignees.map((assignee, idx) => (
                                        <div
                                          key={assignee.user_id || idx}
                                          className={`h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white ring-2 ring-white dark:ring-gray-800 ${getAvatarColor(assignee.email, assignee.first_name)}`}
                                          title={assignee.displayName}
                                        >
                                          {getAssigneeInitials(assignee)}
                                        </div>
                                      ))}
                                    </div>
                                    {avatarAssignees.length === 1 && (
                                      <span className={`text-xs font-medium truncate max-w-[120px] ${isDark ? 'text-gray-200' : 'text-gray-800'}`} title={avatarAssignees[0].displayName}>
                                        {avatarAssignees[0].displayName}
                                      </span>
                                    )}
                                    {avatarAssignees.length > 1 && (
                                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {avatarAssignees.length} assigned
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{assigneeName}</div>
                              );
                            })()}
                            {/* Team name below */}
                            {appointmentTeam?.team_name && (
                              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {appointmentTeam.team_name}{appointmentTeam.leader ? ` · Lead ${appointmentTeam.leader.name}` : ''}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="space-y-1.5">
                            <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{appt.contact_name || workItem.contact_name || 'No contact'}</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{getAppointmentLocationLabel(appt)}</div>
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="space-y-1.5 text-xs">
                            {enRouteMinutes != null && enRouteMinutes > 0 && (
                              <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                En route {enRouteMinutes >= 60 ? `${(enRouteMinutes / 60).toFixed(1)}h` : `${enRouteMinutes}m`}
                                {travelDistanceKm != null && travelDistanceKm > 0 ? ` · ${travelDistanceKm.toFixed(1)} km` : ''}
                              </div>
                            )}
                            {inProgressMinutes != null && inProgressMinutes > 0 && (
                              <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>On site {inProgressMinutes >= 60 ? `${(inProgressMinutes / 60).toFixed(1)}h` : `${inProgressMinutes}m`}</div>
                            )}
                            {actualDuration && appt.custom_fields?.check_out_timestamp && (
                              <div className={isDark ? 'text-emerald-300' : 'text-emerald-700'}>Completed {formatTimeOnly(appt.custom_fields.check_out_timestamp)} · {actualDuration}</div>
                            )}
                            {!actualDuration && !hasActualTimes(appt) && (
                              <div className={isDark ? 'text-gray-500' : 'text-gray-500'}>Updated {formatDateTime(appt.updated_at || appt.created_at)}</div>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenAppointmentModal(appt);
                              }}
                              className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isDark ? 'bg-gray-800 text-gray-100 hover:bg-gray-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Open
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Full Appointment Detail Modal */}
      {viewingAppointment && (() => {
        const apptStatus = viewingAppointment.status;
        const normalizedAppointmentStatus = normalizeWorkflowStatusKey(apptStatus);
        const isReadOnly = normalizedAppointmentStatus === 'closed';
        const readOnlyReasonMap: Record<string, string> = {
          closed: 'This appointment is closed and is now read-only.',
        };
        return (
          <WorkItemDetailModal
            isOpen={viewingAppointmentModalOpen}
            onClose={() => {
              setViewingAppointmentModalOpen(false);
              setViewingAppointment(null);
              // Refresh appointments list in case changes were made
              loadAppointments();
              onUpdate?.();
            }}
            workItem={viewingAppointment}
            readOnly={isReadOnly}
            readOnlyReason={readOnlyReasonMap[normalizedAppointmentStatus] || (isReadOnly ? 'This appointment is in a terminal state and is now read-only.' : undefined)}
            onRefresh={() => {
              loadAppointments();
              onUpdate?.();
            }}
            onDelete={isReadOnly ? undefined : () => {
              setViewingAppointmentModalOpen(false);
              setViewingAppointment(null);
              loadAppointments();
              onUpdate?.();
            }}
          />
        );
      })()}

    </div>
  );
}
